import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Grid, Card, CardContent, Button, Switch, 
  FormControlLabel, Alert, Snackbar, CircularProgress, Divider,
  Chip, IconButton, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { 
  Backup as BackupIcon, CloudDownload, Download, Delete as DeleteIcon, 
  Restore as RestoreIcon, Schedule as ScheduleIcon, Info as InfoIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../../utils/fetchJson';

const BACKED_UP_ITEM_KEYS = [
  'schoolSettings', 'students', 'staff', 'usersRoles',
  'classes', 'subjects', 'enrollments', 'marksGrades',
  'attendanceRecords', 'feeStructures', 'paymentsInvoices',
  'timetables', 'evaluations', 'staffEvaluations',
  'expensesBudgets', 'payroll', 'calendarEvents',
  'guardians', 'announcements', 'scholarshipsDiscounts'
];

const BackupSecurityPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backupSettings, setBackupSettings] = useState({
    autoBackupEnabled: false,
    backupFrequency: 'weekly',
    lastBackupAt: null
  });
  const [backups, setBackups] = useState([]);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreBackupName, setRestoreBackupName] = useState('');
  const [restoring, setRestoring] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const showSnackbar = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  useEffect(() => {
    fetchSettings();
    fetchBackupsList();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetchJson('/api/school-settings/backup-security', { method: 'GET' });
      const data = response?.data || response || {};
      if (data.backupSettings) setBackupSettings(data.backupSettings);
    } catch (error) {
      console.error('Fetch settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBackupsList = async () => {
    try {
      const list = await fetchJson('/api/backups/list', { method: 'GET' });
      const backupArray = Array.isArray(list) ? list : (list?.data || []);
      setBackups(backupArray);
    } catch (error) {
      console.error('[Backups] Failed to fetch backups list:', error);
    }
  };

  const handleBackupSettingChange = (field) => async (event) => {
    const newValue = typeof event.target.checked === 'boolean' ? event.target.checked : event.target.value;
    const updated = { ...backupSettings, [field]: newValue };
    setBackupSettings(updated);
    try {
      await fetchJson('/api/school-settings/backup-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: newValue })
      });
      showSnackbar(t('backupSecurityPage.snack.settingUpdated'));
    } catch (error) {
      showSnackbar(t('backupSecurityPage.errors.settingUpdateFailed'), 'error');
      setBackupSettings(backupSettings);
    }
  };

  const handleBackupFrequencyChange = async (event) => {
    const newFreq = event.target.value;
    const updated = { ...backupSettings, backupFrequency: newFreq };
    setBackupSettings(updated);
    try {
      await fetchJson('/api/school-settings/backup-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backupFrequency: newFreq })
      });
      showSnackbar(t('backupSecurityPage.snack.frequencyUpdated'));
    } catch (error) {
      showSnackbar(t('backupSecurityPage.errors.frequencyUpdateFailed'), 'error');
      setBackupSettings(backupSettings);
    }
  };

  const handleCreateBackup = async () => {
    setSaving(true);
    try {
      await fetchJson('/api/backups/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'manual' })
      });
      showSnackbar(t('backupSecurityPage.snack.created'));
      const now = new Date().toISOString();
      setBackupSettings(prev => ({ ...prev, lastBackupAt: now }));
      try {
        await fetchJson('/api/school-settings/backup-settings', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ lastBackupAt: now })
        });
      } catch (_) {}
      fetchBackupsList();
    } catch (error) {
      showSnackbar(error?.data?.error || t('backupSecurityPage.errors.createFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadBackup = (backupName) => {
    const configuredBaseRaw = (
      process.env.REACT_APP_API_BASE_URL ||
      process.env.VITE_API_BASE_URL ||
      process.env.NEXT_PUBLIC_API_BASE_URL ||
      ''
    ).trim();
    const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
    const isLocalBrowserHost = ['localhost', '127.0.0.1', '0.0.0.0'].includes(browserHost);
    const isLocalNodeEnv = process.env.NODE_ENV === 'development';
    const baseRaw = configuredBaseRaw || ((isLocalBrowserHost || isLocalNodeEnv) ? 'http://localhost:5000' : '');

    const token = localStorage.getItem('accessToken') || localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    const base = (baseRaw || '').replace(/\/$/, '');
    const url = `${base}/api/backups/download/${encodeURIComponent(backupName)}`;
    if (token) {
      fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' })
        .then(res => res.blob())
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.setAttribute('download', backupName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
          showSnackbar(t('backupSecurityPage.snack.downloaded'));
        })
        .catch(() => showSnackbar(t('backupSecurityPage.errors.downloadFailed'), 'error'));
    }
  };

  const handleDeleteBackup = async (backupName) => {
    if (!window.confirm(t('backupSecurityPage.confirmDelete'))) return;
    try {
      await fetchJson(`/api/backups/delete/${encodeURIComponent(backupName)}`, { method: 'DELETE' });
      showSnackbar(t('backupSecurityPage.snack.deleted'));
      fetchBackupsList();
    } catch (error) {
      showSnackbar(t('backupSecurityPage.errors.deleteFailed'), 'error');
    }
  };

  const handleRestorePreview = async (backupName) => {
    setRestoreBackupName(backupName);
    setRestorePreview(null);
    setRestoreDialogOpen(true);
    try {
      const response = await fetchJson('/api/backups/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backupName, confirmRestore: false })
      });
      setRestorePreview(response?.preview || null);
    } catch (error) {
      showSnackbar(t('backupSecurityPage.errors.previewFailed'), 'error');
      setRestoreDialogOpen(false);
    }
  };

  const handleRestoreExecute = async () => {
    if (!restoreBackupName) return;
    setRestoring(true);
    try {
      const response = await fetchJson('/api/backups/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backupName: restoreBackupName, confirmRestore: true })
      });
      showSnackbar(response?.message || t('backupSecurityPage.snack.restored'));
      setRestoreDialogOpen(false);
      setRestorePreview(null);
    } catch (error) {
      showSnackbar(error.message || t('backupSecurityPage.errors.restoreFailed'), 'error');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BackupIcon sx={{ mr: 1, fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h4" component="h1">{t('backupSecurityPage.title')}</Typography>
        <Chip label={t('backupSecurityPage.scopeChip')} size="small" color="info" variant="outlined" sx={{ ml: 2 }} />
      </Box>

      {/* How it works */}
      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
        <strong>{t('backupSecurityPage.howItWorks.title')}</strong> {t('backupSecurityPage.howItWorks.body')} <strong>{t('backupSecurityPage.howItWorks.recommendation')}</strong>
      </Alert>

      <Grid container spacing={3}>
        {/* Backup Settings Card */}
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ScheduleIcon sx={{ mr: 1 }} />
                {t('backupSecurityPage.schedule.title')}
              </Typography>
              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch 
                    checked={backupSettings.autoBackupEnabled || false}
                    onChange={handleBackupSettingChange('autoBackupEnabled')}
                  />
                }
                label={t('backupSecurityPage.schedule.enableAuto')}
              />

              {backupSettings.autoBackupEnabled && (
                <Box sx={{ mt: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('backupSecurityPage.schedule.frequencyLabel')}</InputLabel>
                    <Select
                      value={backupSettings.backupFrequency || 'weekly'}
                      onChange={handleBackupFrequencyChange}
                      label={t('backupSecurityPage.schedule.frequencyLabel')}
                    >
                      <MenuItem value="daily">{t('backupSecurityPage.schedule.frequencies.daily')}</MenuItem>
                      <MenuItem value="weekly">{t('backupSecurityPage.schedule.frequencies.weekly')}</MenuItem>
                      <MenuItem value="biweekly">{t('backupSecurityPage.schedule.frequencies.biweekly')}</MenuItem>
                      <MenuItem value="monthly">{t('backupSecurityPage.schedule.frequencies.monthly')}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}

              {backupSettings.lastBackupAt && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {t('backupSecurityPage.schedule.lastBackup', { date: new Date(backupSettings.lastBackupAt).toLocaleString() })}
                </Alert>
              )}
              
              <Button 
                variant="contained" 
                fullWidth 
                sx={{ mt: 2 }}
                onClick={handleCreateBackup}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={20} /> : <CloudDownload />}
              >
                {saving ? t('backupSecurityPage.schedule.creating') : t('backupSecurityPage.schedule.createManual')}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {t('backupSecurityPage.schedule.manualHint')}
              </Typography>
            </CardContent>
          </Card>

          {/* What gets backed up */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StorageIcon sx={{ mr: 1 }} />
                {t('backupSecurityPage.whatGetsBackedUp.title')}
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Grid container spacing={0.5} sx={{ mt: 1 }}>
                {BACKED_UP_ITEM_KEYS.map((k) => (
                  <Grid item xs={6} key={k}>
                    <Typography variant="caption" color="text.secondary">• {t(`backupSecurityPage.whatGetsBackedUp.items.${k}`)}</Typography>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Backups List Card */}
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <BackupIcon sx={{ mr: 1 }} />
                  {t('backupSecurityPage.backupsList.title')}
                </Typography>
                <Chip label={t('backupSecurityPage.backupsList.count', { count: backups.length })} size="small" color="primary" variant="outlined" />
              </Box>
              <Divider sx={{ my: 2 }} />

              {backups.length === 0 ? (
                <Alert severity="info">
                  {t('backupSecurityPage.backupsList.none')}
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>{t('backupSecurityPage.table.backup')}</strong></TableCell>
                        <TableCell><strong>{t('backupSecurityPage.table.date')}</strong></TableCell>
                        <TableCell><strong>{t('backupSecurityPage.table.size')}</strong></TableCell>
                        <TableCell align="right"><strong>{t('backupSecurityPage.table.actions')}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backups.map((backup, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {backup.phase === 'all'
                                ? t('backupSecurityPage.backupsList.fullSchoolBackup')
                                : t('backupSecurityPage.backupsList.phase', { phase: backup.phase })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {new Date(backup.createdAt).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={t('backupSecurityPage.backupsList.sizeMb', { size: backup.sizeMB || '0.00' })} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <IconButton size="small" color="warning" onClick={() => handleRestorePreview(backup.name)} title={t('backupSecurityPage.tooltips.restore')}>
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="primary" onClick={() => handleDownloadBackup(backup.name)} title={t('backupSecurityPage.tooltips.download')}>
                                <Download fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteBackup(backup.name)} title={t('backupSecurityPage.tooltips.delete')}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>{t('backupSecurityPage.tip.title')}</strong> {t('backupSecurityPage.tip.body')}
              </Alert>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Restore Backup Dialog */}
      <Dialog open={restoreDialogOpen} onClose={() => !restoring && setRestoreDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RestoreIcon color="warning" />
          {t('backupSecurityPage.restoreDialog.title')}
        </DialogTitle>
        <DialogContent>
          {!restorePreview ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>{t('backupSecurityPage.restoreDialog.warningTitle')}</strong> {t('backupSecurityPage.restoreDialog.warningBody')}
              </Alert>

              <Typography variant="subtitle2" gutterBottom>{t('backupSecurityPage.restoreDialog.detailsTitle')}</Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>{t('backupSecurityPage.restoreDialog.typeLabel')}</strong> {restorePreview.metadata?.type === 'school_wide_backup'
                  ? t('backupSecurityPage.backupsList.fullSchoolBackupAllPhases')
                  : (restorePreview.metadata?.phaseCode || 'all')} &bull; 
                <strong>{t('backupSecurityPage.restoreDialog.createdLabel')}</strong> {restorePreview.metadata?.createdAt
                  ? new Date(restorePreview.metadata.createdAt).toLocaleString()
                  : t('backupSecurityPage.restoreDialog.unknown')}
              </Typography>

              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>{t('backupSecurityPage.restoreDialog.recordsTitle')}</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('backupSecurityPage.restoreDialog.dataTypeCol')}</strong></TableCell>
                      <TableCell align="right"><strong>{t('backupSecurityPage.restoreDialog.recordsCol')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(restorePreview.recordCounts || {}).filter(([, v]) => v > 0).map(([key, count]) => (
                      <TableRow key={key}>
                        <TableCell sx={{ textTransform: 'capitalize' }}>
                          {t(`backupSecurityPage.recordTypes.${key}`, { defaultValue: key.replace(/([A-Z])/g, ' $1').trim() })}
                        </TableCell>
                        <TableCell align="right">
                          <Chip label={String(count)} size="small" color="primary" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialogOpen(false)} disabled={restoring}>{t('backupSecurityPage.restoreDialog.cancel')}</Button>
          <Button 
            onClick={handleRestoreExecute} 
            variant="contained" 
            color="warning"
            disabled={restoring || !restorePreview}
            startIcon={restoring ? <CircularProgress size={18} /> : <RestoreIcon />}
          >
            {restoring ? t('backupSecurityPage.restoreDialog.restoring') : t('backupSecurityPage.restoreDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BackupSecurityPage;
